<template>
    <div class="form-edit-page">
        <div class="page-header">
            <div>
                <h1>Pesquisa Avançada de Processos</h1>
                <p>
                    Localize processos usando qualquer informação cadastrada, movimentação
                    ou período de datas.
                </p>
            </div>
        </div>

        <div class="form-card">
            <div class="card-title">Filtros de pesquisa</div>

            <div class="row row-space">
                <div class="field field-grow-3">
                    <label>Pesquisa geral</label>
                    <input
                        v-model="filtros.termo"
                        type="text"
                        class="form-control"
                        placeholder="Número, estabelecimento, município, CNPJ, interessado, produto, rótulo, observação..."
                    />
                </div>

                <div class="field field-grow-1">
                    <label>Tipo de processo</label>
                    <select v-model="filtros.tipoProcesso" class="form-control">
                        <option value="">Todos</option>
                        <option v-for="item in lsTiposProcesso" :key="item" :value="item">
                            {{ item }}
                        </option>
                    </select>
                </div>

                <div class="field field-grow-1">
                    <label>Situação</label>
                    <select v-model="filtros.situacao" class="form-control">
                        <option value="">Todas</option>
                        <option v-for="item in lsSituacoes" :key="item" :value="item">
                            {{ item }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="row row-space">
                <div class="field field-grow-1">
                    <label>Município</label>
                    <select v-model="filtros.municipio" class="form-control">
                        <option value="">Todos</option>
                        <option v-for="item in lsMunicipios" :key="item" :value="item">
                            {{ item }}
                        </option>
                    </select>
                </div>

                <div class="field field-grow-2">
                    <label>Estabelecimento</label>
                    <input
                        v-model="filtros.estabelecimento"
                        type="text"
                        class="form-control"
                        placeholder="Nome do estabelecimento"
                    />
                </div>

                <div class="field field-grow-1">
                    <label>Responsável</label>
                    <select v-model="filtros.responsavel" class="form-control">
                        <option value="">Todos</option>
                        <option v-for="item in lsResponsaveis" :key="item" :value="item">
                            {{ item }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="row row-space">
                <div class="field field-grow-1">
                    <label>Pesquisar pela data</label>
                    <select v-model="filtros.tipoData" class="form-control">
                        <option value="dtAbertura">Abertura</option>
                        <option value="dtProtocolo">Protocolo</option>
                        <option value="dtUltimaMov">Última movimentação</option>
                        <option value="dtConclusao">Conclusão</option>
                        <option value="dtArquivamento">Arquivamento</option>
                    </select>
                </div>

                <div class="field field-grow-1">
                    <label>Data inicial</label>
                    <input v-model="filtros.dataInicial" type="date" class="form-control" />
                </div>

                <div class="field field-grow-1">
                    <label>Data final</label>
                    <input v-model="filtros.dataFinal" type="date" class="form-control" />
                </div>

                <div class="field field-grow-1">
                    <label>Ordenar por</label>
                    <select v-model="filtros.ordenacao" class="form-control">
                        <option value="dtUltimaMov_desc">Última movimentação (mais recente)</option>
                        <option value="dtAbertura_desc">Abertura (mais recente)</option>
                        <option value="dtAbertura_asc">Abertura (mais antiga)</option>
                        <option value="numero_asc">Número do processo</option>
                        <option value="estabelecimento_asc">Estabelecimento</option>
                        <option value="municipio_asc">Município</option>
                    </select>
                </div>
            </div>

            <div class="row row-actions">
                <button class="btn btn-primary" @click="executarPesquisa">
                    Pesquisar
                </button>

                <button class="btn btn-secondary" @click="limparFiltros">
                    Limpar filtros
                </button>
            </div>

            <div class="chips-area" v-if="chipsAtivos.length">
                <span class="chip" v-for="chip in chipsAtivos" :key="chip">
                    {{ chip }}
                </span>
            </div>
        </div>

        <div class="kpi-grid">
            <div class="kpi-card">
                <div class="kpi-label">Total encontrado</div>
                <div class="kpi-value">{{ registrosFiltrados.length }}</div>
            </div>

            <div class="kpi-card">
                <div class="kpi-label">Em análise</div>
                <div class="kpi-value">{{ totalPorSituacao('Em análise') }}</div>
            </div>

            <div class="kpi-card">
                <div class="kpi-label">Deferidos</div>
                <div class="kpi-value">{{ totalPorSituacao('Deferido') }}</div>
            </div>

            <div class="kpi-card">
                <div class="kpi-label">Pendentes</div>
                <div class="kpi-value">{{ totalPorSituacao('Pendente de documentos') }}</div>
            </div>
        </div>

        <div class="form-card">
            <div class="card-title">Resultado da pesquisa</div>

            <div class="table-responsive">
                <table class="table-list">
                    <thead>
                        <tr>
                            <th>Nº Processo</th>
                            <th>Tipo</th>
                            <th>Estabelecimento</th>
                            <th>Município</th>
                            <th>Situação</th>
                            <th>Abertura</th>
                            <th>Últ. Mov.</th>
                            <th>Responsável</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in registrosFiltrados"
                            :key="item.id"
                            class="row-clickable"
                            @click="selecionarProcesso(item)"
                        >
                            <td>{{ item.numero }}</td>
                            <td>{{ item.tipoProcesso }}</td>
                            <td>{{ item.estabelecimento }}</td>
                            <td>{{ item.municipio }}</td>
                            <td>
                                <span class="status-badge" :class="statusClass(item.situacao)">
                                    {{ item.situacao }}
                                </span>
                            </td>
                            <td>{{ formatarData(item.dtAbertura) }}</td>
                            <td>{{ formatarData(item.dtUltimaMov) }}</td>
                            <td>{{ item.responsavel }}</td>
                        </tr>

                        <tr v-if="!registrosFiltrados.length">
                            <td colspan="8" class="table-empty">
                                Nenhum processo encontrado para os filtros informados.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="processoSelecionado" class="form-card">
            <div class="card-title">Detalhes do processo selecionado</div>

            <div class="detail-grid">
                <div class="detail-item">
                    <span class="detail-label">Número</span>
                    <span class="detail-value">{{ processoSelecionado.numero }}</span>
                </div>

                <div class="detail-item">
                    <span class="detail-label">Tipo</span>
                    <span class="detail-value">{{ processoSelecionado.tipoProcesso }}</span>
                </div>

                <div class="detail-item">
                    <span class="detail-label">Estabelecimento</span>
                    <span class="detail-value">{{ processoSelecionado.estabelecimento }}</span>
                </div>

                <div class="detail-item">
                    <span class="detail-label">Município</span>
                    <span class="detail-value">{{ processoSelecionado.municipio }}</span>
                </div>

                <div class="detail-item">
                    <span class="detail-label">Interessado</span>
                    <span class="detail-value">{{ processoSelecionado.interessado }}</span>
                </div>

                <div class="detail-item">
                    <span class="detail-label">CNPJ</span>
                    <span class="detail-value">{{ processoSelecionado.cnpj }}</span>
                </div>

                <div class="detail-item">
                    <span class="detail-label">Situação</span>
                    <span class="detail-value">{{ processoSelecionado.situacao }}</span>
                </div>

                <div class="detail-item">
                    <span class="detail-label">Responsável</span>
                    <span class="detail-value">{{ processoSelecionado.responsavel }}</span>
                </div>

                <div class="detail-item">
                    <span class="detail-label">Produto</span>
                    <span class="detail-value">{{ processoSelecionado.produto }}</span>
                </div>

                <div class="detail-item">
                    <span class="detail-label">Rótulo</span>
                    <span class="detail-value">{{ processoSelecionado.rotulo }}</span>
                </div>

                <div class="detail-item">
                    <span class="detail-label">Abertura</span>
                    <span class="detail-value">{{ formatarData(processoSelecionado.dtAbertura) }}</span>
                </div>

                <div class="detail-item">
                    <span class="detail-label">Última movimentação</span>
                    <span class="detail-value">{{ formatarData(processoSelecionado.dtUltimaMov) }}</span>
                </div>
            </div>

            <div class="detail-observacao">
                <label>Observações / histórico resumido</label>
                <div class="detail-box">
                    {{ processoSelecionado.observacao }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const lsTiposProcesso = [
    'Registro de estabelecimento',
    'Registro de produto',
    'Registro de rótulo',
    'Auto de infração',
    'Advertência',
    'Interdição',
    'Solicitação documental',
];

const lsSituacoes = [
    'Em análise',
    'Deferido',
    'Indeferido',
    'Arquivado',
    'Pendente de documentos',
];

const lsMunicipios = [
    'Araranguá',
    'Sombrio',
    'Meleiro',
    'Turvo',
    'Maracajá',
    'Balneário Arroio do Silva',
];

const lsResponsaveis = [
    'Maria Oliveira',
    'João Pereira',
    'Ana Souza',
    'Carlos Mendes',
];

const filtros = ref({
    termo: '',
    tipoProcesso: '',
    situacao: '',
    municipio: '',
    estabelecimento: '',
    responsavel: '',
    tipoData: 'dtAbertura',
    dataInicial: '',
    dataFinal: '',
    ordenacao: 'dtUltimaMov_desc',
});

const registros = ref([
    {
        id: 1,
        numero: 'PROC-2026/000145',
        tipoProcesso: 'Registro de estabelecimento',
        estabelecimento: 'Agroindústria Vale do Sul',
        municipio: 'Araranguá',
        situacao: 'Em análise',
        responsavel: 'Maria Oliveira',
        interessado: 'Agroindústria Vale do Sul Ltda',
        cnpj: '12.345.678/0001-10',
        produto: 'Linguiça colonial',
        rotulo: 'Vale do Sul Premium',
        dtAbertura: '2026-02-10',
        dtProtocolo: '2026-02-10',
        dtUltimaMov: '2026-03-18',
        dtConclusao: '',
        dtArquivamento: '',
        observacao: 'Processo com documentação principal entregue. Em análise técnica do cadastro e estrutura física.',
    },
    {
        id: 2,
        numero: 'PROC-2026/000122',
        tipoProcesso: 'Registro de produto',
        estabelecimento: 'Laticínios Serra Azul',
        municipio: 'Turvo',
        situacao: 'Deferido',
        responsavel: 'João Pereira',
        interessado: 'Laticínios Serra Azul ME',
        cnpj: '23.456.789/0001-45',
        produto: 'Queijo colonial',
        rotulo: 'Serra Azul Tradicional',
        dtAbertura: '2026-01-22',
        dtProtocolo: '2026-01-23',
        dtUltimaMov: '2026-02-14',
        dtConclusao: '2026-02-14',
        dtArquivamento: '',
        observacao: 'Produto aprovado após análise documental e conferência do memorial descritivo.',
    },
    {
        id: 3,
        numero: 'PROC-2026/000118',
        tipoProcesso: 'Registro de rótulo',
        estabelecimento: 'Apiário Doce Campo',
        municipio: 'Meleiro',
        situacao: 'Pendente de documentos',
        responsavel: 'Ana Souza',
        interessado: 'Apiário Doce Campo',
        cnpj: '34.567.890/0001-90',
        produto: 'Mel silvestre',
        rotulo: 'Doce Campo Natural',
        dtAbertura: '2026-01-18',
        dtProtocolo: '2026-01-18',
        dtUltimaMov: '2026-03-05',
        dtConclusao: '',
        dtArquivamento: '',
        observacao: 'Aguardando envio da versão ajustada da arte final e memorial de rotulagem.',
    },
    {
        id: 4,
        numero: 'PROC-2025/000987',
        tipoProcesso: 'Auto de infração',
        estabelecimento: 'Frigorífico Costa Sul',
        municipio: 'Sombrio',
        situacao: 'Arquivado',
        responsavel: 'Carlos Mendes',
        interessado: 'Frigorífico Costa Sul Ltda',
        cnpj: '45.678.901/0001-33',
        produto: 'Cortes bovinos',
        rotulo: 'Linha Especial',
        dtAbertura: '2025-11-03',
        dtProtocolo: '2025-11-03',
        dtUltimaMov: '2025-12-01',
        dtConclusao: '2025-11-25',
        dtArquivamento: '2025-12-01',
        observacao: 'Processo encerrado após decisão administrativa e cumprimento das determinações.',
    },
    {
        id: 5,
        numero: 'PROC-2026/000151',
        tipoProcesso: 'Solicitação documental',
        estabelecimento: 'Pescados Mar Vivo',
        municipio: 'Balneário Arroio do Silva',
        situacao: 'Em análise',
        responsavel: 'Maria Oliveira',
        interessado: 'Pescados Mar Vivo Ltda',
        cnpj: '56.789.012/0001-11',
        produto: 'Filé de tilápia',
        rotulo: 'Mar Vivo Seleção',
        dtAbertura: '2026-03-01',
        dtProtocolo: '2026-03-01',
        dtUltimaMov: '2026-03-19',
        dtConclusao: '',
        dtArquivamento: '',
        observacao: 'Processo vinculado à atualização cadastral com complementação de licenças e anexos.',
    },
    {
        id: 6,
        numero: 'PROC-2026/000133',
        tipoProcesso: 'Registro de estabelecimento',
        estabelecimento: 'Ovos Campo Feliz',
        municipio: 'Maracajá',
        situacao: 'Indeferido',
        responsavel: 'Ana Souza',
        interessado: 'Ovos Campo Feliz',
        cnpj: '67.890.123/0001-72',
        produto: 'Ovos caipiras',
        rotulo: 'Campo Feliz Premium',
        dtAbertura: '2026-02-02',
        dtProtocolo: '2026-02-03',
        dtUltimaMov: '2026-02-28',
        dtConclusao: '2026-02-28',
        dtArquivamento: '',
        observacao: 'Indeferimento por ausência de requisitos documentais e inconformidades estruturais.',
    },
]);

const processoSelecionado = ref(null);

const registrosFiltrados = computed(() => {
    let lista = [...registros.value];

    const termo = filtros.value.termo.trim().toLowerCase();

    if (termo) {
        lista = lista.filter(item =>
            [
                item.numero,
                item.tipoProcesso,
                item.estabelecimento,
                item.municipio,
                item.situacao,
                item.responsavel,
                item.interessado,
                item.cnpj,
                item.produto,
                item.rotulo,
                item.observacao,
            ]
                .join(' ')
                .toLowerCase()
                .includes(termo)
        );
    }

    if (filtros.value.tipoProcesso) {
        lista = lista.filter(item => item.tipoProcesso === filtros.value.tipoProcesso);
    }

    if (filtros.value.situacao) {
        lista = lista.filter(item => item.situacao === filtros.value.situacao);
    }

    if (filtros.value.municipio) {
        lista = lista.filter(item => item.municipio === filtros.value.municipio);
    }

    if (filtros.value.estabelecimento.trim()) {
        const valor = filtros.value.estabelecimento.trim().toLowerCase();
        lista = lista.filter(item => item.estabelecimento.toLowerCase().includes(valor));
    }

    if (filtros.value.responsavel) {
        lista = lista.filter(item => item.responsavel === filtros.value.responsavel);
    }

    if (filtros.value.dataInicial) {
        lista = lista.filter(item => {
            const valorData = item[filtros.value.tipoData];
            return valorData && valorData >= filtros.value.dataInicial;
        });
    }

    if (filtros.value.dataFinal) {
        lista = lista.filter(item => {
            const valorData = item[filtros.value.tipoData];
            return valorData && valorData <= filtros.value.dataFinal;
        });
    }

    const [campo, direcao] = filtros.value.ordenacao.split('_');

    lista.sort((a, b) => {
        let valorA = a[campo] || '';
        let valorB = b[campo] || '';

        if (typeof valorA === 'string') valorA = valorA.toLowerCase();
        if (typeof valorB === 'string') valorB = valorB.toLowerCase();

        if (valorA < valorB) return direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return direcao === 'asc' ? 1 : -1;
        return 0;
    });

    return lista;
});

const chipsAtivos = computed(() => {
    const chips = [];

    if (filtros.value.termo) chips.push(`Texto: ${filtros.value.termo}`);
    if (filtros.value.tipoProcesso) chips.push(`Tipo: ${filtros.value.tipoProcesso}`);
    if (filtros.value.situacao) chips.push(`Situação: ${filtros.value.situacao}`);
    if (filtros.value.municipio) chips.push(`Município: ${filtros.value.municipio}`);
    if (filtros.value.estabelecimento) chips.push(`Estabelecimento: ${filtros.value.estabelecimento}`);
    if (filtros.value.responsavel) chips.push(`Responsável: ${filtros.value.responsavel}`);
    if (filtros.value.dataInicial) chips.push(`De: ${formatarData(filtros.value.dataInicial)}`);
    if (filtros.value.dataFinal) chips.push(`Até: ${formatarData(filtros.value.dataFinal)}`);

    return chips;
});

function executarPesquisa() {
    processoSelecionado.value = registrosFiltrados.value.length
        ? registrosFiltrados.value[0]
        : null;
}

function limparFiltros() {
    filtros.value = {
        termo: '',
        tipoProcesso: '',
        situacao: '',
        municipio: '',
        estabelecimento: '',
        responsavel: '',
        tipoData: 'dtAbertura',
        dataInicial: '',
        dataFinal: '',
        ordenacao: 'dtUltimaMov_desc',
    };

    processoSelecionado.value = null;
}

function selecionarProcesso(item) {
    processoSelecionado.value = item;
}

function totalPorSituacao(situacao) {
    return registrosFiltrados.value.filter(item => item.situacao === situacao).length;
}

function formatarData(data) {
    if (!data) return '-';

    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
}

function statusClass(situacao) {
    if (situacao === 'Deferido') return 'status-success';
    if (situacao === 'Em análise') return 'status-warning';
    if (situacao === 'Pendente de documentos') return 'status-info';
    if (situacao === 'Indeferido') return 'status-danger';
    if (situacao === 'Arquivado') return 'status-neutral';
    return '';
}
</script>

<style scoped>
.form-edit-page {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-header h1 {
    margin: 0;
    font-size: 24px;
    color: #1f2937;
}

.page-header p {
    margin: 4px 0 0 0;
    color: #6b7280;
}

.form-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 16px;
}

.card-title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 14px;
}

.row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.row-space {
    margin-bottom: 12px;
}

.row-actions {
    margin-top: 8px;
}

.field {
    display: flex;
    flex-direction: column;
    min-width: 180px;
}

.field-grow-1 {
    flex: 1 1 180px;
}

.field-grow-2 {
    flex: 2 1 280px;
}

.field-grow-3 {
    flex: 3 1 420px;
}

.field label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
}

.form-control {
    height: 38px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 0 12px;
    font-size: 14px;
    outline: none;
    background: #fff;
}

.form-control:focus {
    border-color: #2563eb;
}

.btn {
    height: 38px;
    padding: 0 16px;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
}

.btn-primary {
    background: #2563eb;
    color: #fff;
}

.btn-secondary {
    background: #e5e7eb;
    color: #111827;
}

.chips-area {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 14px;
}

.chip {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
    padding: 6px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
}

.kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
}

.kpi-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 14px;
}

.kpi-label {
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 4px;
}

.kpi-value {
    font-size: 28px;
    font-weight: 700;
    color: #111827;
}

.table-responsive {
    width: 100%;
    overflow-x: auto;
}

.table-list {
    width: 100%;
    border-collapse: collapse;
}

.table-list th,
.table-list td {
    padding: 12px 10px;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    font-size: 14px;
}

.table-list th {
    background: #f9fafb;
    color: #374151;
    font-weight: 700;
}

.row-clickable {
    cursor: pointer;
}

.row-clickable:hover {
    background: #f9fafb;
}

.table-empty {
    text-align: center;
    color: #6b7280;
    padding: 18px;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 700;
}

.status-success {
    background: #dcfce7;
    color: #166534;
}

.status-warning {
    background: #fef3c7;
    color: #92400e;
}

.status-info {
    background: #dbeafe;
    color: #1d4ed8;
}

.status-danger {
    background: #fee2e2;
    color: #991b1b;
}

.status-neutral {
    background: #e5e7eb;
    color: #374151;
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 12px;
}

.detail-item {
    display: flex;
    flex-direction: column;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px;
}

.detail-label {
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 4px;
}

.detail-value {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
}

.detail-observacao {
    margin-top: 14px;
}

.detail-observacao label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
}

.detail-box {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px;
    color: #111827;
    line-height: 1.5;
}

@media (max-width: 768px) {
    .page-header {
        align-items: flex-start;
        flex-direction: column;
        gap: 8px;
    }
}
</style>